import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { ShoppingList, ShoppingListItem } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Sorts shopping lists by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all lists have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * @param lists - Array of shopping lists to sort
 * @returns New array with lists sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortListsByCreatedAt = (lists: ShoppingList[]): ShoppingList[] => {
  return [...lists].sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Sorts shopping list items by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all items have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * This function handles both temporary IDs (temp-*) and real IDs, ensuring
 * proper sorting regardless of sync status.
 *
 * @param items - Array of shopping list items to sort
 * @returns New array with items sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortItemsByCreatedAt = (items: ShoppingListItem[]): ShoppingListItem[] => {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Orders items for list-detail rendering.
 *
 * The base app convention is newest-created item first. When grouping is enabled,
 * checked items are partitioned below unchecked items without changing the
 * newest-first ordering inside either group.
 */
export const orderItemsForListDisplay = (
  items: Array<ShoppingListItem | null | undefined>,
  groupDoneItems: boolean,
): ShoppingListItem[] => {
  const validItems = items.filter((item): item is ShoppingListItem => Boolean(item?.id));
  const sortedItems = sortItemsByCreatedAt(validItems);

  if (!groupDoneItems) {
    return sortedItems;
  }

  const uncheckedItems = sortedItems.filter(item => !item.checked);
  const checkedItems = sortedItems.filter(item => item.checked);

  return [...uncheckedItems, ...checkedItems];
};

/**
 * Translates a message that might be a translation key from the backend
 * @param message - The message from backend (could be a translation key or plain text)
 * @param t - The translation function from useTranslation
 * @param interpolationValues - Optional interpolation values for the translation
 * @returns The translated message or the original message if it's not a translation key
 */
export const translateBackendMessage = (
  message: string | undefined,
  t: (key: string, options?: any) => string,
  interpolationValues?: Record<string, any>
): string => {
  if (!message) return '';

  // Check if the message looks like a translation key (contains dots and common patterns)
  if (
    message.includes('.') && (
      message.includes('stripe.') ||
      message.includes('errors.') ||
      message.includes('messages.') ||
      message.includes('referrals.')
    )
  ) {
    try {
      // Try to translate it as a key with interpolation values if provided
      const translated = t(message, interpolationValues);

      // If the translation returns the same key, it means the key doesn't exist
      // In that case, return the original message
      if (translated === message) {
        console.warn(`Translation key not found: ${message}`);
        return message;
      }

      return translated;
    } catch (error) {
      console.error(`Translation error for key ${message}:`, error);
      // If translation fails, return the original message
      return message;
    }
  }

  // If it doesn't look like a translation key, return as is
  return message;
};
